<template>
  <div class="container grid-lg">
    <Loading v-if="stateGlobal.appLoading" />
    <Modal ref="dlgAppEnvironment" :showTitle="false">
      <template #body>
        <h2>Verzija aplikacije</h2>
        <br />
        Na voljo je nova verzija aplikacije ({{ stateGlobal.appData?.jsVersion }}).<br />
        Prosimo osvežite brskalnik (CTRL+R oz. CTRL+F5).
      </template>
    </Modal>
    <header :class="{ short: $user }">
      <div class="logo">
        <div class="padding-top4">
          <a href="" title="Vstopna stran"><img alt="Vstopna stran" src="@/assets/logo.svg" /></a>
        </div>
        <div class="title">
          Poročanje o plačilih iz javnega naročanja
          <div style="color: red; font-size: 16px" v-if="appEnvironmentDesc">{{ appEnvironmentDesc }}</div>
        </div>
      </div>
      <template v-if="$user">
        <div class="user">
          <div>
            <i class="fa fa-cog" aria-hidden="true"></i>
          </div>
          <div class="padding-left5">
            <a href @click.prevent="$router.push({ name: 'userProfile' })">&nbsp;{{ $user.fullName }}</a>
          </div>
          <div class="padding-left10 padding-right10">|</div>
          <div>
            <a href @click.prevent="logout">ODJAVA</a>
          </div>
        </div>
      </template>
    </header>
    <main role="main">
      <router-view />
      <template v-if="errorMap($appErrors, '.').length > 0">
        <div class="inner padding-top1">
          <div class="toast toast-error margin-top20" v-if="errorMap($appErrors, '.')">
            {{ $appErrors[0].message }}
          </div>
        </div>
      </template>
    </main>
    <footer>
      <div class="columns">
        <div class="column col-4">
          <div><img class="logo" alt="logo" src="@/assets/logo.svg" /></div>
          <div class="padding-top10 padding-bottom40"></div>
          <div class="padding-top30"><img alt="mfrac" src="@/assets/mfrac.png" /></div>
          <div class="padding-top20"><img alt="eu" src="@/assets/eu.png" /></div>
        </div>
        <div class="column col-8">
          <div class="columns" v-html="footer"></div>
        </div>
      </div>
      <Accessibility />
    </footer>
    <Toaster ref="toast" :theme="toastTheme.theme">{{ toastTheme.text }}</Toaster>
  </div>
</template>
<script lang="ts">
import Modal from './components/common/Modal.vue'
import Loading from './components/common/Loading.vue'
import { defineComponent, computed, ref, getCurrentInstance } from 'vue'
import { useApi } from './lib/api'
import { useStore } from 'vuex'
import { GlobalState } from './lib/types'
import { errorMap } from '@/lib/filters'
import { useRouter, useRoute } from 'vue-router'
import Accessibility from './components/Accessibility.vue'
import Toaster from './components/common/Toaster.vue'

export default defineComponent({
  components: { Loading, Modal, Accessibility, Toaster },

  setup() {
    const _root = getCurrentInstance()
    const _api = useApi()
    const _store = useStore<GlobalState>()
    const _route = useRoute()
    const _router = useRouter()
    const dlgAppEnvironment = ref()
    const footer = ref()
    const toast = ref()
    const toastTheme = ref({ theme: 'green', text: null })
    const state = ref({
      showNotAuth: false,
      domainName: ''
    })

    const ping = async () => {
      const res = await _api.get<any>('ping')
      if (res.status === 200) {
        _store.commit('appData', res.data.appSettings)
        if (res.data.isAuthenticated) {
          _store.commit('user', res.data.user)

          let url = ''
          if (_route.meta.requestUrl) {
            const reqUrl = _route.meta.requestUrl as string
            url = reqUrl.substring(reqUrl.indexOf('#/') + 2)
          }

          if (url.length > 0) {
            _router.push({ path: '/' + url })
          } else {
            _router.push({ name: 'reportList' })
          }
        } else {
          state.value.showNotAuth = true
        }

        const info = await import('../package.json')

        if (_store.state.appData?.jsVersion !== info.version) {
          dlgAppEnvironment.value.open()
        }
      } else {
        _store.commit('appErrors', [{ key: '.', message: 'Prišlo je do napake.' }])
      }
    }

    const logout = async () => {
      await _router.push({ name: 'index' })
      await _api.post('logout', null)
      _store.commit('user', null)
    }

    const getFooter = async () => {
      const res = await _api.cache.get<any>('footer')
      footer.value = res.data.text
    }

    const appEnvironmentDesc = computed(() => {
      switch (_store.state.appData?.appEnvironment) {
        case 0:
          return 'Razvojno okolje'
        case 1:
          return 'TESTNO OKOLJE'
        default:
          return null
      }
    })

    _root?.appContext.config.globalProperties.emitter.on('showToaster', (t: any) => {
      toastTheme.value = t
      toast.value.show()
    })

    getFooter()
    ping()

    return { stateGlobal: _store.state, state, appEnvironmentDesc, dlgAppEnvironment, logout, errorMap, toast, toastTheme, footer }
  }
})
</script>

<style lang="scss">
@import '../node_modules/font-awesome/css/font-awesome.css';
@import '../node_modules/tippy.js/dist/tippy.css';
//@import url('https://fonts.googleapis.com/css?family=Open+Sans&subset=cyrillic-ext,latin-ext');
@import 'global';

::-ms-clear {
  display: none;
}

*:focus {
  box-shadow: none !important;
  border-color: #c5d0d6 !important;
  border-width: 0 0 1px;
}

@font-face {
  font-family: 'Republika';
  src: url('assets/fonts/republika-regular-webfont.eot?v=4');
  src: url('assets/fonts/republika-regular-webfont.eot?#iefix') format('embedded-opentype'), url('assets/fonts/republika-regular-webfont.woff2?v=4') format('woff2'), url('assets/fonts/republika-regular-webfont.woff?v=4') format('woff'), url('assets/fonts/republika-regular-webfont.ttf?v=4') format('truetype'), url('assets/fonts//republika-regular-webfont.svg#Republika') format('svg');
  font-weight: normal;
  font-style: normal;
}

@font-face {
  font-family: 'Republika';
  src: url('assets/fonts/republika-bold-webfont.eot?v=4');
  src: url('assets/fonts/republika-bold-webfont.eot?#iefix') format('embedded-opentype'), url('assets/fonts/republika-bold-webfont.woff2?v=4') format('woff2'), url('assets/fonts/republika-bold-webfont.woff?v=4') format('woff'), url('assets/fonts/republika-bold-webfont.ttf?v=4') format('truetype'), url('assets/fonts//republika-bold-webfont.svg#Republika') format('svg');
  font-weight: bold;
  font-style: normal;
}

body {
  overflow-y: scroll;
  overflow-x: auto;
  font-size: 12px;
  font-family: $fontBody;
  color: $colFont;
  background-color: #e5e5e5;
}

.nowrap {
  white-space: nowrap;
}

a,
a.active,
a:active,
a:focus,
a:hover,
a:visited {
  color: $colGreenLighter;
  text-decoration: none;
}

a:focus,
.button:focus {
  outline: 1px auto;
}

h2 {
  font-size: 16px;
  line-height: 44px;
  color: $colGreenLight;
  font-weight: bold;
  margin-bottom: 0;
}

h3 {
  font-size: 20px;
  color: $colGreenLight;
  font-weight: bold;
}

h4 {
  font-size: 15px;
  font-weight: 600;
}

header {
  height: 97px;
  background: url('./assets/header.png') no-repeat 100% 0 $colGreen;
  color: #fff;
  padding: 22px 49px;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: bold;

  .logo {
    flex: 1;
    display: flex;
    align-items: center;

    .title {
      margin-left: 29px;
    }
  }

  .user {
    display: flex;
    color: #1b4656;

    a {
      color: #1b4656;
    }

    .fa {
      font-size: 16px;
    }
  }

  &.short {
    height: 66px;

    img {
      height: 42px;
    }
  }

  .flex1 {
    flex: 1;
  }

  ul.menu-top {
    margin-left: 158px;
    list-style: none;

    li {
      padding-right: 25px;
      display: inline-block;
      margin-top: 0;
    }

    a {
      font-size: 18px;
      color: #fff;
      font-weight: normal;
      text-transform: uppercase;

      &.active {
        font-weight: bold;
      }
    }
  }
}

footer {
  position: relative;
  min-height: 299px;
  background: url('./assets/footer.png') no-repeat 0 0 $colGreen;
  padding: 35px 40px;
  color: #fff;

  .logo {
    height: 48px;
  }

  a {
    color: #fff !important;
  }
}

.inner {
  background-color: #f4f4f4;
  padding: 36px;
}

.box {
  padding: 25px 20px;
  background-color: #fff;
  box-shadow: 4px 0 24px rgba(117, 131, 142, 0.15);
  border-radius: 3px;
  color: #2e2e2e;

  a {
    color: #0e98ba;
  }

  &.grey {
    background-color: #f4f4f4;
    box-shadow: none;
    border-radius: 0;
    padding: 40px 50px;
  }

  &.green {
    background: rgba(143, 197, 209, 0.15);
    box-shadow: none;
  }
}

.relative {
  position: relative !important;
}

.line-through {
  text-decoration: line-through;
}

.bgWhite {
  background-color: #fff;
}

.colGreen {
  color: $colGreen;
}

.colBorder {
  color: $colBorder;
}

.colRed {
  color: red;
}

.tippy-box {
  font-size: 11px;
}

.container.grid-lg {
  max-width: initial;
  width: 1216px;
}

.font14 {
  font-size: 14px;
}

.text-right-important {
  text-align: right !important;
}

.modal-md .modal-container {
  max-width: 990px;
}

.modal-title {
  margin-left: -32px;
  margin-right: -32px;
  padding-left: 32px;
  padding-right: 32px;
  padding-bottom: 10px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: 0 8px 4px rgba(117, 131, 142, 0.04);
}

.modal-container {
  max-height: 100% !important;
}

.modal-overlay {
  background: rgba(0, 0, 0, 0.7) !important;
}

.pagination {
  .page-item {
    border: solid 1px $colGreen;
    border-radius: 4px;
  }

  .page-item.active a {
    background-color: $colGreen;
  }
}

.loading::after {
  border-top-color: $colGreenLighter;
  border-left-color: $colGreenLighter;
  border-bottom-color: $colGreenLighter;
}

.form-input,
.form-select {
  border: 1px solid $colBorder;
  font-size: 12px !important;
  height: 32px !important;
  border-radius: 4px;
}

.form-select.disabled,
.form-select:disabled {
  background-color: #eef0f3 !important;
  cursor: not-allowed !important;
}

a.button,
.button {
  display: inline-block;
  cursor: pointer;
  min-width: 100px;
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 10px;
  padding-right: 10px;
  border: 1px solid $colGreen;
  border-radius: 3px;
  background-color: $colGreen;
  transition: border-radius 200ms ease;
  color: #fff;
  line-height: 24px;
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  font-size: 10px !important;

  &.small {
    font-size: 10px;
    min-width: auto;
    padding: 0 5px;
    height: 25px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &.sipass {
    background: url('./assets/sipass.png') no-repeat 15px center #f60;
    background-size: 12px;
    border-color: #f60;
    color: #fff;
  }

  &.trans {
    background-color: transparent;
    color: $colGreen;
  }

  &.red {
    background-color: $colRed;
    border-color: $colRed;
  }
}

.table {
  font-size: 11px !important;

  thead {
    background-color: rgba(143, 197, 209, 0.15);
  }

  th {
    cursor: pointer;
    border-bottom-width: 0;
  }

  &.table-hover tbody tr:hover {
    background: rgba(143, 197, 209, 0.05);
  }

  &.compact {
    td {
      padding: 2px 5px;
      //border: 0;
    }

    th {
      padding: 5px;
      cursor: default;
    }
  }

  tr.viewed {
    td:first-child {
      font-weight: bold !important;
    }
  }
}

hr {
  background-color: $colBorder;
  height: 1px;
  border: none;
}

.small {
  font-size: 10px;
}

.cursor-hand {
  cursor: pointer;
}

.cursor-help {
  cursor: help;
}

.text-upper {
  text-transform: uppercase;
}

.form-label {
  font-size: 12px;
}

.justify-content-center {
  justify-content: center;
}

.align-items-center {
  align-items: center;
}

.form-checkbox .form-icon {
  &::before {
    border-color: $colGreen !important;
  }
}

.form-checkbox input:checked + .form-icon {
  background: #fff;
  border-color: #dddfdf;
  color: #000;

  &::before {
    border-color: #000;
  }
}

.form-radio input:checked + .form-icon {
  background-color: $colGreen;
  border-color: $colGreen;
}

.form-input-hint {
  color: red !important;
  font-size: 11px;
  margin: 0;
  overflow: hidden;
  cursor: help;
  position: absolute;
  z-index: 10;

  &:hover {
    height: auto;
  }
}

.has-error {
  .form-input,
  .form-select,
  .form-input.is-danger,
  .form-radio .form-icon,
  .btn-group .btn {
    border: solid 1px red !important;
    // background-color: $colLightRed;
  }
}

.from-label {
  text-transform: uppercase;
  font-weight: bold;
}

.toast-error {
  background-color: red !important;
  font-weight: bold;
}

.tabColActions {
  width: 100px;
  white-space: nowrap;
}

.d-table {
  display: table;
}

.d-table-cell {
  display: table-cell;
}

@media screen and (max-width: 576px) {
  header {
    .title,
    .logo .end {
      display: none;
    }
  }
}

@for $i from -40 through 100 {
  @each $pos in top, left, right, bottom {
    .padding-#{$pos}#{$i} {
      padding-#{$pos}: #{$i}px;
    }

    .margin-#{$pos}#{$i} {
      margin-#{$pos}: #{$i}px;
    }
  }
}

.contrast * {
  background-image: none !important;
  background-color: #000 !important;
  color: $colContrast !important;

  a,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: $colContrast !important;
  }

  .button {
    background-color: $colContrast !important;
    border-color: $colContrast !important;
    color: #000 !important;
  }

  input[type='text'] {
    background-color: #000 !important;
    color: $colContrast !important;

    &::placeholder {
      color: $colContrast !important;
    }
  }

  .form-input,
  .form-select {
    border-color: $colContrast !important;
  }

  .form-checkbox .form-icon {
    &::before {
      border-color: $colContrast !important;
    }
  }

  .loading::after {
    border-top-color: $colContrast;
    border-left-color: $colContrast;
    border-bottom-color: $colContrast;
  }
}

.fontSizeM * {
  font-size: 16px !important;

  .link-help {
    font-size: 16px !important;
  }

  h4.rule-bottom,
  .alert h4 * {
    font-size: 21px !important;
  }

  h1.app {
    font-size: 47px !important;
  }
}

.fontSizeL * {
  font-size: 18px !important;

  .link-help {
    font-size: 18px !important;
  }

  h4.rule-bottom,
  .alert h4 * {
    font-size: 23px !important;
  }

  h1.app {
    font-size: 50px !important;
  }
}
</style>
